import { Company } from "../../../../interfaces/Company";
import { Project } from "../../../../interfaces/Project";
import { UpdateHoursDetail } from "../../../../interfaces/UpdateHoursDetail";
import React, { useEffect, useMemo, useState } from "react";
import InputMask from "react-input-mask";
import api from "../../../../services/axios";

import { toast } from "react-toastify";
import { getTasksForTimesheet } from "../../../../services/projectTaskApi";
import { TimesheetTaskOption } from "../../../../interfaces/ProjectTask";

interface EditHourModalProps {
  isModalOpen: boolean;
  timeSheetid: number | undefined;
  timeSheetDetailId: number | undefined;
  day: number;
  month: number;
  year: number;
  initialValue: string;
  initialProjectValue: number;
  project: Project[];
  initialCompanyValue: number;
  company: Company[];
  dsActivity: string;
  /** Tarefa já vinculada ao apontamento (para exibir/preservar o vínculo ao editar). */
  initialTaskId?: number | null;
  initialTaskTitle?: string | null;
  onClose: () => void;
  onUpdate: () => void;
}

const EditHourModal: React.FC<EditHourModalProps> = ({
  isModalOpen,
  timeSheetid,
  timeSheetDetailId,
  day,
  month,
  year,
  initialValue,
  initialProjectValue,
  initialCompanyValue,
  project,
  company,
  dsActivity,
  initialTaskId,
  initialTaskTitle,
  onClose,
  onUpdate,
}) => {
  const toggleModal = () => {
    onClose();
  };

  // Tarefa do portal contra a qual as horas são apontadas (0 = nenhuma). Inicia com a já vinculada.
  const [selectedTaskId, setSelectedTaskId] = useState<number>(initialTaskId ?? 0);
  const [time, setTime] = useState(initialValue);
  const [tasks, setTasks] = useState<TimesheetTaskOption[]>([]);
  const [selectedCompany, setSelectedCompany] =
    useState<number>(initialCompanyValue);
  const [selectedProject, setSelectedProject] =
    useState<number>(initialProjectValue);
  const [description, setDescription] = useState(dsActivity);

  const [onDelete, setDelete] = useState(false);

  // Opções de tarefa: as do portal + a já vinculada (mesmo que não esteja na lista do colaborador).
  const taskOptions = useMemo(() => {
    const opts = tasks.map((t) => ({
      taskId: t.taskId,
      title: t.title,
      estimatedHours: t.estimatedHours as number | undefined,
    }));
    if (initialTaskId && !opts.some((o) => o.taskId === initialTaskId)) {
      opts.unshift({ taskId: initialTaskId, title: initialTaskTitle ?? `Tarefa #${initialTaskId}`, estimatedHours: undefined });
    }
    return opts;
  }, [tasks, initialTaskId, initialTaskTitle]);

  // Carrega as tarefas do portal atribuídas ao colaborador no projeto selecionado.
  useEffect(() => {
    if (!selectedProject) {
      setTasks([]);
      return;
    }
    getTasksForTimesheet(selectedProject)
      .then((data) => setTasks(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("Failed to load portal tasks:", err);
        setTasks([]);
      });
  }, [selectedProject]);

  const currentDate = new Date();
  const formattedDate = `${year ?? currentDate.getFullYear()}-${(
    month ?? currentDate.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}-${(day ?? currentDate.getDate())
    .toString()
    .padStart(2, "0")}`;
  const [date, setDate] = useState(formattedDate);

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTime(e.target.value);
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setDescription(e.target.value);
  };

  const handleCompanyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCompany(Number(event.target.value));
    setSelectedProject(0);
    setSelectedTaskId(0); // Limpa a tarefa selecionada ao trocar de empresa
  };

  const filteredProjects = selectedCompany
    ? project.filter((p) => p.clientId === selectedCompany)
    : [];

  const handleProjectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedProject(Number(event.target.value));
    setSelectedTaskId(0); // Limpa a tarefa ao trocar de projeto
  };

  const handleOnDelete = () => {
    setDelete(true);
  };

  const handleOnSave = () => {
    setDelete(false);
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDate(event.target.value);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const dateParts = date.split("-");
    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10);
    const day = parseInt(dateParts[2], 10);
    let hoursWorked = time;

    // Trava: não permite lançar horas em data futura.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (new Date(year, month - 1, day) > today) {
      toast.error("Não é permitido lançar horas em datas futuras.", {
        position: "top-right",
        autoClose: 4000,
      });
      return;
    }

    // Título da tarefa selecionada (mantido também no campo legado 'tasks').
    const selectedTask = tasks.find((t) => t.taskId === selectedTaskId);
    const selectedTaskNames = selectedTask ? [selectedTask.title] : [];

    // Monta o objeto para enviar na requisição, vinculando a tarefa do portal (projectTaskId).
    const updateHoursDetail: UpdateHoursDetail & { tasks?: string[] } = {
      timeSheetId: timeSheetid!,
      timeSheetDetailId: timeSheetDetailId!,
      projectId: selectedProject,
      clientId: selectedCompany,
      day: day,
      month: month,
      year: year,
      hoursWorked: hoursWorked.length === 5 ? hoursWorked + ":00" : hoursWorked,
      dsActivity: description,
      tasks: selectedTaskNames,
      projectTaskId: selectedTaskId || null,
    };

    if (onDelete === true) {
      deleteWorkedHours(updateHoursDetail);
      onClose();
      onUpdate();
    } else if (timeSheetid !== 0 && timeSheetid !== undefined) {
      updateWorkedHours(updateHoursDetail);
      onClose();
      onUpdate();
    } else {
      addWorkedHours(updateHoursDetail);
      onClose();
      onUpdate();
    }
  };

  async function addWorkedHours(hoursData: UpdateHoursDetail & { tasks?: string[] }) {
    try {
      const response = await api.post("/TimeManagement/WorkedHours", hoursData);

      if (response.status !== 200) {
        let errorMessage =
          response.data.message || JSON.stringify(response.data);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }

      toast.success("Success to adding Workhours", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      return response.data;
    } catch (error) {
      console.error("Error adding worked hours:", error);
      toast.error("Error adding worked hours", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  }

  async function updateWorkedHours(hoursData: UpdateHoursDetail & { tasks?: string[] }) {
    try {
      const response = await api.put("/TimeManagement/WorkedHours", hoursData);

      if (response.status !== 200) {
        let errorMessage =
          response.data.message || JSON.stringify(response.data);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }

      toast.success("Success to update Workhours", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      return response.data;
    } catch (error) {
      console.error("Error updating worked hours:", error);
      toast.error("Error updating worked hours", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  }

  async function deleteWorkedHours(hoursData: UpdateHoursDetail & { tasks?: string[] }) {
    try {
      const response = await api.delete("/TimeManagement/WorkedHours", {
        data: hoursData,
      });
      toast.success("Success to delete Workhours", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return response.data;
    } catch (error) {
      console.error("Error deleting worked hours:", error);
      toast.error("Error deleting worked hours", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  }

  if (!isModalOpen) {
    return null;
  }

  const fieldCls =
    "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-navy-900 dark:text-white";
  const labelCls = "mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-300";

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 p-4 backdrop-blur-sm"
        onClick={toggleModal}
      >
        <div
          className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white nina-surface shadow-dv-lg dark:bg-navy-800"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-white/10">
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">
              Apontar horas
            </h3>
            <button
              type="button"
              aria-label="Fechar"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-navy-700 dark:text-gray-400 dark:hover:bg-navy-700 dark:hover:text-white"
              onClick={toggleModal}
            >
              <svg
                className="h-3.5 w-3.5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
                aria-hidden="true"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
            </button>
          </div>
          <div className="px-6 py-5">
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="hour-client" className={labelCls}>
                  Cliente <span className="text-dv-magenta-600">*</span>
                </label>
                <select
                  id="hour-client"
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  className={fieldCls}
                  required
                >
                  <option value={0}>Selecione…</option>
                  {company.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.legalName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="hour-project" className={labelCls}>
                  Projeto <span className="text-dv-magenta-600">*</span>
                </label>
                <select
                  id="hour-project"
                  value={selectedProject}
                  onChange={handleProjectChange}
                  disabled={selectedCompany === 0}
                  className={fieldCls}
                  required
                >
                  <option value={0}>
                    {selectedCompany === 0 ? "Selecione um cliente primeiro…" : "Selecione…"}
                  </option>
                  {filteredProjects.map((option) => (
                    <option key={option.projectId} value={option.projectId}>
                      {option.nmProject}
                    </option>
                  ))}
                </select>
              </div>
              {/* Tarefa do portal contra a qual as horas são apontadas */}
              <div>
                <label htmlFor="hour-task" className={labelCls}>
                  Tarefa
                </label>
                <select
                  id="hour-task"
                  value={selectedTaskId}
                  onChange={(e) => setSelectedTaskId(Number(e.target.value))}
                  disabled={selectedProject === 0}
                  className={fieldCls}
                >
                  <option value={0}>
                    {selectedProject === 0
                      ? "Selecione um projeto primeiro…"
                      : taskOptions.length === 0
                      ? "Nenhuma tarefa disponível"
                      : "Sem tarefa (ex.: reunião)"}
                  </option>
                  {taskOptions.map((task) => (
                    <option key={task.taskId} value={task.taskId}>
                      {task.title}
                      {task.estimatedHours != null
                        ? ` (${task.estimatedHours}h estimadas)`
                        : ""}
                    </option>
                  ))}
                </select>
                <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                  Tarefas cadastradas no portal atribuídas a você neste projeto.
                </span>
              </div>
              <div>
                <label htmlFor="description" className={labelCls}>
                  Descrição <span className="text-dv-magenta-600">*</span>
                </label>
                <textarea
                  id="description"
                  className={fieldCls}
                  required
                  rows={5}
                  value={description}
                  onChange={handleDescriptionChange}
                />
              </div>
              <div>
                <label htmlFor="date" className={labelCls}>
                  Data <span className="text-dv-magenta-600">*</span>
                </label>
                <input
                  id="date"
                  type="date"
                  max={new Date().toISOString().slice(0, 10)}
                  className={`${fieldCls} tabular-nums`}
                  required
                  value={date}
                  onChange={handleDateChange}
                />
              </div>
              <div>
                <label htmlFor="hour" className={labelCls}>
                  Horas trabalhadas <span className="text-dv-magenta-600">*</span>
                </label>
                <InputMask
                  id="hour"
                  className={`${fieldCls} tabular-nums`}
                  required
                  mask="99:99"
                  value={time}
                  onChange={handleTimeChange}
                />
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  type="submit"
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
                  name="save"
                  onClick={handleOnSave}
                  disabled={
                    selectedCompany === 0 ||
                    selectedProject === 0 ||
                    description.length < 5 ||
                    date.length < 5 ||
                    time.length < 2
                  }
                >
                  Salvar
                </button>
                <button
                  type="submit"
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-red-200 px-5 py-2.5 text-sm font-semibold text-red-600 transition-all hover:bg-red-50 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-500/30 dark:text-red-300 dark:hover:bg-red-900/20"
                  name="delete"
                  onClick={handleOnDelete}
                  disabled={
                    selectedCompany === 0 ||
                    selectedProject === 0 ||
                    description.length < 5 ||
                    date.length < 5 ||
                    time.length < 2
                  }
                >
                  Excluir
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditHourModal;
